import type {
  AddServiceOrderNoteBody,
  AddServiceOrderNoteResponse,
  AssignReplacementRobotBody,
  BatchRecallServiceOrdersBody,
  BatchRecallServiceOrdersResponse,
  BulkAssignServiceOrdersBody,
  BulkAssignServiceOrdersResponse,
  BulkTransitionServiceOrdersBody,
  BulkTransitionServiceOrdersResponse,
  CancelServiceOrderBody,
  CancelServiceOrderResponse,
  CreateServiceOrderResponse,
  ListServiceOrdersResponse,
  ServiceOrderCategory,
  ServiceOrderDetailDto,
  ServiceOrderStatus,
  ServiceOrderSummaryDto,
  ServiceSeverity,
  TransitionServiceOrderBody,
  TransitionServiceOrderResponse,
} from '@bhops/shared';
import { apiClient } from './client';

// plan-field-service-orders.md §F-6 — windows API client.
//
// Mirrors apps/windows/src/api/reports.ts. Endpoints:
//
//   GET    /service-orders                — paginated list
//   GET    /service-orders/:id             — full detail
//   POST   /service-orders                 — manual create (operator path)
//   POST   /service-orders/:id/transitions — drive state machine
//   POST   /service-orders/:id/cancel      — terminal CANCELLED + reason
//   POST   /service-orders/:id/notes       — timeline note (NOTE_ADDED)
//
// Manual create takes a clientId (UUID v4) for idempotency on
// (reportedById, clientId); call sites generate one per submit.

export type SortByField =
  | 'createdAt'
  | 'triageDeadlineAt'
  | 'resolveDeadlineAt'
  | 'verifyDeadlineAt'
  | 'scheduledFor';

export interface ListServiceOrdersQuery {
  status?: ServiceOrderStatus;
  severity?: ServiceSeverity;
  category?: ServiceOrderCategory;
  customerId?: string;
  robotId?: string;
  // batch-8 — 공개 채널 (PUB) 필터. backend `ServiceOrder.origin` 컬럼 기반.
  origin?: 'INTERNAL' | 'PUBLIC';
  // §F-7: pass '__none__' to filter to unassigned rows.
  assignedToId?: string;
  // round7 §1-5 (D4) — soft-delete 행 포함(ADMIN 전용).
  includeDeleted?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
  // §F-7 — sort axis. Defaults to createdAt desc on the server.
  sortBy?: SortByField;
  sortDir?: 'asc' | 'desc';
}

// §F-7 — sentinel value the server reads as "assignedToId IS NULL".
export const UNASSIGNED_SENTINEL = '__none__';

export interface CreateServiceOrderBody {
  clientId: string;
  robotId: string;
  customerId: string;
  storeId?: string;
  category: ServiceOrderCategory;
  severity: ServiceSeverity;
  description: string;
  internalNotes?: string;
  assignedToId?: string;
  scheduledFor?: string;
}

export const serviceOrdersApi = {
  async list(query: ListServiceOrdersQuery = {}): Promise<ListServiceOrdersResponse> {
    const { data } = await apiClient.get<ListServiceOrdersResponse>('/service-orders', {
      params: query,
    });
    return data;
  },
  async get(id: string): Promise<ServiceOrderDetailDto> {
    const { data } = await apiClient.get<ServiceOrderDetailDto>(`/service-orders/${id}`);
    return data;
  },
  async create(body: CreateServiceOrderBody): Promise<CreateServiceOrderResponse> {
    const { data } = await apiClient.post<CreateServiceOrderResponse>('/service-orders', body);
    return data;
  },
  async transition(
    id: string,
    body: TransitionServiceOrderBody,
  ): Promise<TransitionServiceOrderResponse> {
    const { data } = await apiClient.post<TransitionServiceOrderResponse>(
      `/service-orders/${id}/transitions`,
      body,
    );
    return data;
  },
  // round7 §1-5 (D4) — soft-delete (매니저 이상). ADMIN 의 includeDeleted
  // 목록 + restore-deleted 로 복구 가능.
  async remove(id: string): Promise<ServiceOrderSummaryDto> {
    const { data } = await apiClient.delete<ServiceOrderSummaryDto>(
      `/service-orders/${encodeURIComponent(id)}`,
    );
    return data;
  },
  async cancel(id: string, body: CancelServiceOrderBody): Promise<CancelServiceOrderResponse> {
    const { data } = await apiClient.post<CancelServiceOrderResponse>(
      `/service-orders/${id}/cancel`,
      body,
    );
    return data;
  },
  // round7 §1-5 (D5) — 등급 재분류(SLA 마감 재계산 + 사유 감사).
  async changeSeverity(
    id: string,
    body: { severity: ServiceSeverity; reason: string },
  ): Promise<ServiceOrderSummaryDto> {
    const { data } = await apiClient.patch<ServiceOrderSummaryDto>(
      `/service-orders/${encodeURIComponent(id)}/severity`,
      body,
    );
    return data;
  },
  async bulkAssign(body: BulkAssignServiceOrdersBody): Promise<BulkAssignServiceOrdersResponse> {
    const { data } = await apiClient.post<BulkAssignServiceOrdersResponse>(
      '/service-orders/bulk-assign',
      body,
    );
    return data;
  },
  async bulkTransition(
    body: BulkTransitionServiceOrdersBody,
  ): Promise<BulkTransitionServiceOrdersResponse> {
    const { data } = await apiClient.post<BulkTransitionServiceOrdersResponse>(
      '/service-orders/bulk-transition',
      body,
    );
    return data;
  },
  // 계획 §8-2 — 상세 타임라인의 메모 입력칸. `id` 는 클라이언트가 만드는
  // 멱등키라 재시도해도 이벤트가 중복 생성되지 않는다(서버 upsert).
  async addNote(id: string, body: AddServiceOrderNoteBody): Promise<AddServiceOrderNoteResponse> {
    const { data } = await apiClient.post<AddServiceOrderNoteResponse>(
      `/service-orders/${encodeURIComponent(id)}/notes`,
      body,
    );
    return data;
  },
  // S3 §3.6 — ECN 부품 추적(recall)에서 선택한 운영 로봇들에 ServiceOrder 일괄 생성.
  async batchRecall(body: BatchRecallServiceOrdersBody): Promise<BatchRecallServiceOrdersResponse> {
    const { data } = await apiClient.post<BatchRecallServiceOrdersResponse>(
      '/service-orders/batch-recall',
      body,
    );
    return data;
  },
  // S3 §3.9 — 제조팀이 로봇 교체(ROBOT_REPLACE) SO 에 출고 대기 로봇 할당.
  // 성공 시 robot READY_TO_SHIP → INSTALL_PENDING + replacementRobotId 설정.
  async assignReplacementRobot(
    id: string,
    body: AssignReplacementRobotBody,
  ): Promise<ServiceOrderSummaryDto> {
    const { data } = await apiClient.post<ServiceOrderSummaryDto>(
      `/service-orders/${encodeURIComponent(id)}/replacement-robot`,
      body,
    );
    return data;
  },
};

// ── Admin sub-API ────────────────────────────────────────────────
//
// plan-field-service-orders.md §3.3. Routes are gated server-side
// via @Roles(UserRole.ADMIN); this client just hits them and lets
// the API decide. We kept the admin surface in its own object so
// non-admin callers don't have it dangling on the day-to-day
// `serviceOrdersApi` shape.

// Restore target — must be a non-terminal status. The DTO server-
// side has the same allow-list; we mirror it here so a typo
// surfaces at compile time.
export type RestoreTargetStatus = Exclude<
  ServiceOrderStatus,
  'CLOSED' | 'CANCELLED' | 'RESOLVED' | 'VERIFIED'
>;

export interface RestoreServiceOrderBody {
  to?: RestoreTargetStatus;
  reason: string;
}

// v0.5.x — admin reassign-customer body (plan §3.3, last of the
// admin trio). Move a non-terminal SO to a different customer
// when ops created the row under the wrong account (typo, merger,
// rename). humanId is preserved server-side.
//
// `storeId`:
//   - omit / `null` / empty → clear the existing store pointer
//     (the old store almost certainly belongs to the OLD customer
//     and would leave the row in an FK-inconsistent state).
//   - provide a non-empty string → server validates it belongs
//     to the new customer in the same ownerEntity.
export interface ReassignCustomerServiceOrderBody {
  customerId: string;
  storeId?: string | null;
  reason: string;
}

// v0.5.x — bulk variant. Up to 100 ids share the same target
// customer / store / reason. Per-row failures (terminal status,
// missing row, FK mismatch) surface in result.failed[]; successes
// accumulate in result.succeeded[].
export interface BulkReassignCustomerServiceOrdersBody {
  ids: string[];
  customerId: string;
  storeId?: string | null;
  reason: string;
}

export const adminServiceOrdersApi = {
  /**
   * POST /admin/service-orders/:id/restore
   *
   * Pulls a CLOSED / CANCELLED row back into the live queue.
   * Body carries a required `reason` (1-2048 chars) and an
   * optional `to` (default OPEN; allow-list of non-terminal
   * statuses pinned by the server DTO).
   *
   * 200 OK with the restored summary; 400 if the row is in a
   * non-terminal status; 403 if the caller isn't ADMIN.
   */
  async restore(id: string, body: RestoreServiceOrderBody): Promise<ServiceOrderSummaryDto> {
    const { data } = await apiClient.post<ServiceOrderSummaryDto>(
      `/admin/service-orders/${encodeURIComponent(id)}/restore`,
      body,
    );
    return data;
  },
  // round7 §1-5 (D4) — soft-delete 복구(ADMIN). deletedAt 해제만 수행.
  async restoreDeleted(id: string): Promise<ServiceOrderSummaryDto> {
    const { data } = await apiClient.post<ServiceOrderSummaryDto>(
      `/admin/service-orders/${encodeURIComponent(id)}/restore-deleted`,
    );
    return data;
  },

  /**
   * POST /admin/service-orders/:id/reassign-customer
   *
   * Move a non-terminal SO to a different customer (+ optional
   * store). Body:
   *   - `customerId` (required, cuid) — new customer
   *   - `storeId`    (optional)       — new store; must belong
   *                                      to the new customer
   *   - `reason`     (required, 1-2048 chars) — audit string
   *
   * 200 OK with the updated summary; 400 if terminal or FK
   * targets don't resolve in the SO's ownerEntity; 403 if the
   * caller isn't ADMIN.
   *
   * Side effects: customerId + storeId flip, a typed
   * CUSTOMER_REASSIGNED ServiceOrderEvent is written with the
   * from/to pair + reason so the timeline UI can render the
   * reassignment distinctly.
   */
  async reassignCustomer(
    id: string,
    body: ReassignCustomerServiceOrderBody,
  ): Promise<ServiceOrderSummaryDto> {
    const { data } = await apiClient.post<ServiceOrderSummaryDto>(
      `/admin/service-orders/${encodeURIComponent(id)}/reassign-customer`,
      body,
    );
    return data;
  },

  /**
   * POST /admin/service-orders/bulk-reassign-customer
   *
   * Bulk variant of `reassignCustomer`. Up to 100 ids share the
   * same target customer / store / reason. The server iterates
   * per-row using the same validation as the single endpoint;
   * per-row failures surface in `result.failed[]`. Use when a
   * customer rename / merger touches a wave of open SOs.
   *
   * 200 OK with `{ succeeded, failed }`; 400 on DTO validation
   * (too many ids, missing customerId/reason); 403 if caller
   * isn't ADMIN.
   */
  async bulkReassignCustomer(
    body: BulkReassignCustomerServiceOrdersBody,
  ): Promise<BulkAssignServiceOrdersResponse> {
    const { data } = await apiClient.post<BulkAssignServiceOrdersResponse>(
      '/admin/service-orders/bulk-reassign-customer',
      body,
    );
    return data;
  },
};

export type { ServiceOrderSummaryDto, ServiceOrderDetailDto };
